// Create / edit a product brand. The name IS the value stored on
// master_products.brand (no key/label split), so the BE refuses a rename
// while live products carry the brand (409, toasted by the update hook) —
// the helper text warns about that instead of disabling the field, since
// renaming an unused brand (typo fix) is legitimate.

import {
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Text,
  useToast,
} from '@elkaso-app/web-design';
import { ProductBrand, useCreateProductBrandApi, useUpdateProductBrandApi } from 'apis/product-brands';
import { useEffect, useState } from 'react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  brand?: ProductBrand; // present -> edit, absent -> create
}

interface FormState {
  name: string;
  isActive: boolean;
}

const emptyForm = (): FormState => ({ name: '', isActive: true });
const fromBrand = (brand: ProductBrand): FormState => ({ name: brand.name, isActive: brand.isActive });

export const BrandFormModal = ({ isOpen, onClose, brand }: Props) => {
  const toast = useToast();
  const isEdit = !!brand;
  const [form, setForm] = useState<FormState>(emptyForm());

  useEffect(() => {
    if (isOpen) setForm(brand ? fromBrand(brand) : emptyForm());
  }, [isOpen, brand]);

  const set = <K extends keyof FormState>(key: K, val: FormState[K]) => setForm((f) => ({ ...f, [key]: val }));

  const onDone = () => {
    toast({ title: `Brand ${isEdit ? 'updated' : 'created'}`, status: 'success' });
    onClose();
  };
  const onError = (error: any) => {
    // hooks toast server messages; this is the network-error fallback
    if (!error?.response?.data?.message) {
      toast({ title: `Failed to ${isEdit ? 'update' : 'create'} brand`, status: 'error' });
    }
  };

  const createApi = useCreateProductBrandApi({ onSuccess: onDone, onError });
  const updateApi = useUpdateProductBrandApi({ onSuccess: onDone, onError });
  const isSaving = createApi.isLoading || updateApi.isLoading;

  const submit = () => {
    const name = form.name.trim();
    if (!name) {
      toast({ title: 'Name is required', status: 'warning' });
      return;
    }

    if (isEdit && brand) {
      updateApi.mutate({ id: brand.id, data: { name, isActive: form.isActive } });
    } else {
      createApi.mutate({ name, isActive: form.isActive });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='md'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isEdit ? 'Edit Brand' : 'New Brand'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl isRequired mb='3'>
            <FormLabel fontSize='sm'>Name</FormLabel>
            <Input
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder='e.g. Almarai'
              autoFocus={!isEdit}
            />
            {isEdit && (
              <FormHelperText>
                Stored on products verbatim — renaming is refused while any product uses this brand.
              </FormHelperText>
            )}
          </FormControl>
          <Flex align='center' gap='2'>
            <Switch isChecked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} colorScheme='green' />
            <Text fontSize='sm'>Active</Text>
          </Flex>
        </ModalBody>
        <ModalFooter gap='2'>
          <Button variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme='red' onClick={submit} isLoading={isSaving}>
            {isEdit ? 'Save Changes' : 'Create Brand'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
